import { useCallback, useEffect, useState } from "react";
import { membersState } from "../../store/atom";
import { useRecoilState } from "recoil";
import {
  createMember,
  deleteMember,
  getMembers,
  updateMemberOrder,
  updateMember,
} from "../../api/members";
import UpIcon from "../../components/icons/UpIconNew";
import DownIcon from "../../components/icons/DownIconNew";
import styled from "styled-components";
import CreateMemberModal from "../../components/MemberEditModal";
import ConfirmDeleteModal from "../../components/ConfirmDeleteModal";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  margin-bottom: 100px;
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 50px;
  width: 100%;
`;
const OrderButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 100px;
  font-size: 18px;
  line-height: 1.5;
`;

const SelectedMemberContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  width: 100%;

  > p {
    font-weight: bold;
    color: #2aa4d5;
    min-width: 60px;
  }
`;

const OrderChangeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  width: 100%;
  min-width: 180px;

  > span {
    margin-right: 10px;
  }
`;

const OrderButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3C3E45;
  transition: all 0.3s ease;
  padding: 2px;
  border-radius: 50%;
  width: 36px;
  height: 36px;

  &:hover {
    color: var(--primary-c);
  }
`;


const WriteButton = styled.button`
  display: inline-block;
  width: 122px;
  height: 40px;
  background-color: #2aa4d5;
  color: #fff;
  font-weight: 700;
  line-height: 1.5;
  text-align: center;
  border-radius: 15px;
  margin-left: 100px;
  font-size: 16px;
`;

const TableContainer = styled.section`
  width: 100%;
  height: 900px;
  overflow-y: auto;
  font-size: 16px;
`;

const TableEl = styled.table`
  width: 100%;
  border-top: 3px solid #2aa4d5;
  border-bottom: 2px solid #cfd0d1;
`;

const TableHead = styled.thead`
  background-color: #e9f6fb;
  font-weight: 700;
  line-height: 1.5;
  letter-spacing: -0.63px;
  color: #4e4e4e;
  width: 100%;

`;

const TableBody = styled.tbody`
  font-weight: 400;
  color: #4e4e4e;
  width: 100%;
  height: 800px;
  overflow-y: auto;

`;

const TableRowEl = styled.tr`
  display: flex;
  border-bottom: 1px solid #e5e5e5;
  padding: 0 10px;
  width: 100%;

  &:last-child {
    border-bottom: none;
  }
`;

const TableBodyRow = styled(TableRowEl)`
  width: 100%;

  &:hover {
    background-color: rgba(234, 234, 234, 0.7);
  }
`;

const TableHeadItem = styled.th`
  padding: 16px 8px;
`;

const TableHeadItemNo = styled(TableHeadItem)`
  width: 70px;
`;

const TableHeadItemPosition = styled(TableHeadItem)`
  width: 140px;
  justify-content: center;

`;

const TableHeadItemName = styled(TableHeadItem)`
  width: 100px;
`;

const TableHeadItemRole = styled(TableHeadItem)`
  width: 240px;
`;

const TableHeadItemEmail = styled(TableHeadItem)`
  width: 100%;
  max-width: 250px;
`;

const TableHeadItemManage = styled(TableHeadItem)`
width: 140px;
justify-content: center;
`;

const TableBodyItem = styled.td`
  display: flex;
  align-items: center;
  padding: 10px 0;
`;
const TableEmptyMessage = styled(TableRowEl)`
  justify-content: center;
`;

const TableBodyItemNo = styled(TableBodyItem)`
  width: 70px;
  justify-content: center;
`;

const TableBodyItemPosition = styled(TableBodyItem)`
  width: 140px;
  text-align: center;
  justify-content: center;
  span {
    cursor: pointer;
  }
`;

const TableBodyItemName = styled(TableBodyItem)`
  width: 100px;
  justify-content: center;
`;

const TableBodyItemRole = styled(TableBodyItem)`
  width: 240px;
  justify-content: center;
`;

const TableBodyItemEmail = styled(TableBodyItem)`
  justify-content: center;
  width: 100%;
  max-width: 250px;
`;

const TableBodyItemManage = styled(TableBodyItem)`
  width: 140px;
  justify-content: center;
  display: flex;
  gap: 8px;
`;

const Button = styled.button`
  cursor: pointer;
  padding: 0 10px;
  height: 36px;
  border-radius: 5px;
  color: #fff;
  text-align: center;
`;

const EditButton = styled(Button)`
  background-color: #186fb2;
`;
const DeleteButton = styled(Button)`
  background-color: #3d3d3d;
`;

export function Members() {
  const [members, setMembers] = useRecoilState(membersState);
  const [selectedMember, setSelectedMember] = useState(-1);
  const [editingMemberId, setEditingMemberId] = useState(-1);
  const [openCreateMemberModal, setOpenCreateMemberModal] = useState(false);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);

  const handleWriteButtonClick = () => {
    setEditingMemberId(-1);
    setOpenCreateMemberModal(true);
  }

  const fetchMembers = useCallback(async () => {
    const data = await getMembers();
    setMembers(data.members);
  }, [setMembers]);

  const handleOrderUp = useCallback(async () => {
    const index = members.findIndex((member) => member.id === selectedMember);
    if (index === 0) return;
    await updateMemberOrder(selectedMember, index - 1);
    fetchMembers();
  }, [selectedMember, members, fetchMembers]);

  const handleOrderDown = useCallback(async () => {
    if (selectedMember === -1) return;
    const index = members.findIndex((member) => member.id === selectedMember);
    if (index === members.length - 1) return;
    await updateMemberOrder(selectedMember, index + 1);
    fetchMembers();
  }, [selectedMember, members, fetchMembers]);

  const handleCreateMember = useCallback(
    async (member) => {
      await createMember(member);
      fetchMembers();
      setOpenCreateMemberModal(false);
    },
    [fetchMembers]
  );

  const handleDeleteMember = useCallback(
    async (id) => {
      setSelectedMember(id);
      setIsOpenDeleteModal(true);
      // fetchMembers();
    },
    [fetchMembers]
  );

  const handleConfirmDelete = async (isConfirmed) => {
    setIsOpenDeleteModal(false);
    if (isConfirmed) {
      try {
        await deleteMember(selectedMember);
        fetchMembers();
      } catch (error) {
        console.error("구성원 삭제 실패:", error);
      }
    }
  };

  const handleEditMember = (id) => {
    setEditingMemberId(id);
    setOpenCreateMemberModal(true);
  };

  const handleUpdateMember = useCallback(
    async (member) => {
      await updateMember(member.id, member);
      fetchMembers();
      setOpenCreateMemberModal(false);
      setEditingMemberId(-1);
    },
    [fetchMembers]
  );

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  return (
    <Container>
      <ButtonContainer>

        <OrderButtonContainer>
          <SelectedMemberContainer>
            선택 항목 :
            {(() => {
              const selectedMemberName = members.find(member => member.id === selectedMember)?.name || "";
              return selectedMemberName ? (
                <p>
                  {selectedMemberName}
                </p>
              ) : <p></p>;
            })()}
          </SelectedMemberContainer>
          <OrderChangeContainer>
            <span>순서 변경</span>
            <OrderButton onClick={handleOrderUp}>
              <UpIcon height="36px" width="36px" />
            </OrderButton>
            <OrderButton onClick={handleOrderDown}>
              <DownIcon height="36px" width="36px" />
            </OrderButton>
          </OrderChangeContainer>
        </OrderButtonContainer>
        <WriteButton onClick={handleWriteButtonClick}>
          구성원 추가
        </WriteButton>
      </ButtonContainer>
      <TableContainer>
        <TableEl>
          <TableHead>
            <TableRowEl>
              <TableHeadItemNo>No.</TableHeadItemNo>
              <TableHeadItemPosition>근무지</TableHeadItemPosition>
              <TableHeadItemName>이름</TableHeadItemName>
              <TableHeadItemRole>직책</TableHeadItemRole>
              <TableHeadItemEmail>이메일</TableHeadItemEmail>
              <TableHeadItemManage>관리</TableHeadItemManage>
            </TableRowEl>
          </TableHead>
          <TableBody>
            {members.length === 0 ? (
              <TableEmptyMessage>
                <TableBodyItem>구성원이 존재하지 않습니다.</TableBodyItem>
              </TableEmptyMessage>
            ) : (
              members.map((member, index) => (
                <MemberRow
                  key={member.id}
                  no={index + 1}
                  {...member}
                  onEdit={handleEditMember}
                  onDelete={handleDeleteMember}
                  selectedMember={selectedMember}
                  setSelectedMember={setSelectedMember}
                />
              )))}
          </TableBody>
        </TableEl>
      </TableContainer>
      {
        openCreateMemberModal && (
          <CreateMemberModal
            onSave={
              editingMemberId === -1 ? handleCreateMember : handleUpdateMember
            }
            onClose={() => setOpenCreateMemberModal(false)}
            memberId={editingMemberId}
            handleEdit={handleEditMember}
          />
        )
      }
      {
        isOpenDeleteModal && (
          <ConfirmDeleteModal onConfirm={handleConfirmDelete} />
        )
      }

    </Container >
  );
}

function MemberRow({
  onEdit,
  onDelete,
  selectedMember,
  setSelectedMember,
  ...props
}) {
  const isSelected = selectedMember === props.id;

  const handleClick = () => {
    if (isSelected) {
      setSelectedMember(-1);
    } else {
      setSelectedMember(props.id);
    }
  };


  return (
    <TableBodyRow
      onClick={handleClick}
      style={{
        ...(isSelected && { backgroundColor: "lightgray" }),
        cursor: "pointer",
      }}
    >
      <TableBodyItemNo>{props.no}</TableBodyItemNo>
      <TableBodyItemPosition>{props.workplace}</TableBodyItemPosition>
      <TableBodyItemName>{props.name}</TableBodyItemName>
      <TableBodyItemRole>{props.role}</TableBodyItemRole>
      <TableBodyItemEmail>{props.email}</TableBodyItemEmail>
      <TableBodyItemManage>
        <EditButton onClick={(e) => {
          e.stopPropagation();
          onEdit(props.id);
        }}>수정</EditButton>
        <DeleteButton onClick={(e) => {
          e.stopPropagation();
          onDelete(props.id);
        }}>삭제</DeleteButton>
      </TableBodyItemManage>
    </TableBodyRow>
  );
}


